import { TableData } from 'Molecules/NestedTable';
import { isFrequencyYearly, MetricsTableData } from '../MetricAnswers.hooks';
import {
  InviteMembersModal,
  TeamMember,
  TeamMembersFilterOption,
  useTeamMembers,
} from 'containers/TeamMembers';
import { useMemo } from 'react';
import {
  Avatar,
  Box,
  HStack,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { ProjectLeaderSelect } from 'containers/Esrs/pieces/Assessment/EsrsConfig/ProjectLeaderSelect';
import { User } from 'models';
import { AvatarSize } from 'Atoms';
import { useGetDatapointValues } from '../MetricsTable/MetricsTableComponents/InputFields/QuantitativeInputs';
import { Typography } from 'Tokens';
import { getUsernameColor } from 'Organisms/UserAvatar/UserAvatar.utils';
import { EmptyOwner } from 'Atoms/EmptyOwner';
import { UserAvatar } from 'Organisms';
import { useHandleTaskUpdate } from '../Metrics';
import { FrequencyEnums, QUARTERS_ARRAY, TimePeriodsEnums } from '../../Requirement';
import { useParams } from 'react-router-dom';

export const MetricOwnerSelect = ({
  row,
  selectedQuarter,
  companyReportingUnit,
  isBorderless = false,
  isGroup,
  size,
}: {
  row: TableData<MetricsTableData>;
  selectedQuarter: string;
  companyReportingUnit?: string;
  isBorderless?: boolean;
  isGroup?: boolean;
  size?: AvatarSize;
}) => {
  const { answer } = useGetDatapointValues(row, companyReportingUnit);
  const { reportingUnitId } = useParams();
  const {
    isOpen: isInvitationModalOpen,
    onOpen: onInvitationModalOpen,
    onClose: onInvitationModalClose,
  } = useDisclosure();

  const dataPoint = useMemo(
    () => answer?.datapoints.find((dp) => dp.timeframe === selectedQuarter),
    [answer]
  );

  const { members } = useTeamMembers(TeamMembersFilterOption.All);
  const dpTaskOwner = useMemo(() => {
    return dataPoint?.task?.owner;
  }, [dataPoint, answer, members, selectedQuarter]);

  const handleTaskUpdate = useHandleTaskUpdate(
    [
      {
        metricRef: row.metric.reference,
        tags: row.tags,
        timeframe:
          row.metric.materialMetrics[0]?.frequency === FrequencyEnums.yearly
            ? TimePeriodsEnums.year
            : (QUARTERS_ARRAY.find((quarter) => quarter === selectedQuarter) ?? ''),
        disclosureRequirementRef: row.metric.disclosureRequirementRef,
        tagCombination: row.tagCombination,
      },
    ],
    dpTaskOwner?.id,
    reportingUnitId ?? companyReportingUnit
  );

  const updateDatapoint = (member: TeamMember) => {
    handleTaskUpdate('Responsible', { responsible: member.id });
  };

  return (
    <>
      <HStack spacing="4px" onClick={(e) => e.stopPropagation()}>
        <ProjectLeaderSelect
          onChange={updateDatapoint}
          defaultProjectLeader={dpTaskOwner}
          avatarText={dpTaskOwner?.displayName ?? dpTaskOwner?.email}
          isBorderless={isBorderless}
          members={members}
          onInvitationModalOpen={onInvitationModalOpen}
          rounded
          small
          size={size}
        />
        <InviteMembersModal
          isOpen={isInvitationModalOpen}
          onClose={onInvitationModalClose}
          withSubsidiaries={isGroup}
        />
      </HStack>
    </>
  );
};

export const MetricOwnerAvatar = ({
  row,
  selectedQuarter,
  companyReportingUnit,
}: {
  row: TableData<MetricsTableData>;
  selectedQuarter: string;
  companyReportingUnit?: string;
}) => {
  const { dataPointsPerQuarter, dataPointPerYear } = useGetDatapointValues(
    row,
    companyReportingUnit
  );
  const { isOpen, onToggle } = useDisclosure();

  const isYearly = useMemo(() => isFrequencyYearly(row.metric), [row]);

  const quarterlyDataPoint = useMemo(
    () => dataPointsPerQuarter.find((dp) => dp.field === selectedQuarter)?.value,
    [selectedQuarter, dataPointsPerQuarter]
  );

  const dataPoint = useMemo(
    () => (isYearly ? dataPointPerYear : quarterlyDataPoint),
    [isYearly, dataPointPerYear, quarterlyDataPoint]
  );
  const dpOwner = useMemo(() => dataPoint?.owner, [dataPoint]);

  const taskOwner = useMemo(() => dataPoint?.task?.owner, [dataPoint]);

  const hideImage = useMemo(
    () =>
      taskOwner?.avatarUrl?.includes('default=blank') || taskOwner?.avatarUrl?.includes('d=blank'),
    [taskOwner]
  );

  const dueDate = useMemo(() => {
    const dueDateStr = dataPoint?.task?.dueDate;
    if (!dueDateStr) return null;

    const date = new Date(dueDateStr);

    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }, [dataPoint]);

  return taskOwner ? (
    <Popover isOpen={isOpen}>
      <PopoverAnchor>
        <Avatar
          onMouseEnter={onToggle}
          onMouseLeave={onToggle}
          name={taskOwner.displayName}
          src={hideImage ? '' : taskOwner.avatarUrl}
          bgColor={getUsernameColor(taskOwner.displayName)}
          size="xs"
        />
      </PopoverAnchor>
      <PopoverContent bg="bg.accent" border="none" w="fit-content" boxShadow="none !important">
        <PopoverArrow bg="bg.accent" border="none" />
        <VStack alignItems="start" gap="2px" padding="8px">
          <Typography variant="bodyStrong" color="text.onAccent">
            {taskOwner.displayName}
          </Typography>

          <Typography color="text.onAccent" variant="micro">
            {!!dueDate ? `Due ${dueDate}` : 'No deadline'}
          </Typography>
        </VStack>
      </PopoverContent>
    </Popover>
  ) : dpOwner ? (
    <Box width="22px" borderRadius="50%" border="1px solid" color="black">
      <UserAvatar user={dpOwner as Pick<User, 'displayName' | 'avatarUrl'>} size="xs" />
    </Box>
  ) : (
    <EmptyOwner size="20px" />
  );
};
